import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import styles from './SettingsModal.module.css';
import { useTheme } from '../contexts/ThemeContext';
import { getCurrentUser } from '../utils/authService';

Modal.setAppElement('#root');

const SettingsModal = ({ isOpen, onClose, onSave }) => {
  const { theme, updateTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('settings');
  const [tokenData, setTokenData] = useState({ balance: 0, used: 0 });
  const [accountData, setAccountData] = useState({
    plan_status: 'active_free',
    plan_name: 'Free',
    token_allocation: '50,000',
    canCancel: false
  });
  const [settings, setSettings] = useState({
    theme: 'light',
    fontSize: 'medium',
    autoSave: true,
    notificationEnabled: true,
    language: 'en'
  });

  useEffect(() => {
    if (isOpen) {
      fetchTokenData();
      fetchAccountData();
    }
  }, [isOpen]);

  const fetchAccountData = () => {
    const user = getCurrentUser();
    if (user && user.plan_status) {
      const planInfo = getPlanInfo(user.plan_status);
      setAccountData(planInfo);
    }
  };

  const getPlanInfo = (status) => {
    switch(status) {
      case 'active_pro':
        return { 
          plan_status: status,
          plan_name: 'Pro', 
          token_allocation: '500,000',
          canCancel: true,
          statusLabel: 'Active'
        };
      case 'pending_pro':
        return { 
          plan_status: status,
          plan_name: 'Pro', 
          token_allocation: '500,000',
          canCancel: false,
          statusLabel: 'Pending'
        };
      case 'active_free':
        return { 
          plan_status: status,
          plan_name: 'Free', 
          token_allocation: '50,000',
          canCancel: false,
          statusLabel: 'Active'
        };
      case 'pending_free':
        return { 
          plan_status: status,
          plan_name: 'Free', 
          token_allocation: '50,000',
          canCancel: false,
          statusLabel: 'Pending'
        };
      default:
        return { 
          plan_status: 'active_free',
          plan_name: 'Free', 
          token_allocation: '50,000',
          canCancel: false,
          statusLabel: 'Active'
        };
    }
  };

  const fetchTokenData = async () => {
    const user = getCurrentUser();
    try {
      const [balanceRes, transactionsRes] = await Promise.all([
        fetch(`/api/tokens/balance/${user.user_id}`, {
          credentials: 'include'
        }),
        fetch(`/api/tokens/transactions/${user.user_id}`, {
          credentials: 'include'
        })
      ]);
      
      const [balance, transactions] = await Promise.all([
        balanceRes.json(),
        transactionsRes.json()
      ]);
  
      setTokenData({
        balance: balance.current_balance,
        used: balance.total_used,
        transactions
      });
    } catch (error) {
      console.error('Error fetching token data:', error);
    }
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  const handleCancelSubscription = () => {
    onClose();
    window.location.href = '/cancel-subscription';
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      style={{
        overlay: {
          zIndex: 1000000000000
        },
        content: {
          zIndex: 1000000000001
        }
      }}
    >
      <div className={styles.content}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'settings' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'account' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
        </div>

        {activeTab === 'settings' && (
          <>
            <div className={styles.section}>
              <h3>Appearance</h3>
              <div className={styles.setting}>
                <label>Theme</label>
                <select
                  value={theme.name}
                  onChange={(e) => updateTheme(e.target.value)}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="newMetal">New Metal</option>
                  <option value="betternote">Better Note</option>
                  <option value="everGreen">everGreen</option>
                  <option value="bloomingmeadows">Blooming</option>
                  <option value="ringmaster">Ring Master</option>
                  <option value="system">System Default</option>
                </select>
              </div>

              <div className={styles.setting}>
                <label>Font Size</label>
                <select
                  value={settings.fontSize}
                  onChange={(e) => setSettings({...settings, fontSize: e.target.value})}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>

            <div className={styles.section}>
              <h3>Editor</h3>
              <div className={styles.setting}>
                <label>
                  <input
                    type="checkbox"
                    checked={settings.autoSave}
                    onChange={(e) => setSettings({...settings, autoSave: e.target.checked})}
                  />
                  Enable Auto-Save
                </label>
              </div>
            </div>

            <div className={styles.section}>
              <h3>Notifications</h3>
              <div className={styles.setting}>
                <label>
                  <input
                    type="checkbox"
                    checked={settings.notificationEnabled}
                    onChange={(e) => setSettings({...settings, notificationEnabled: e.target.checked})}
                  />
                  Enable Notifications
                </label>
              </div>
            </div>

            <div className={styles.section}>
              <h3>Language</h3>
              <div className={styles.setting}>
                <select
                  value={settings.language}
                  onChange={(e) => setSettings({...settings, language: e.target.value})}
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                </select>
              </div>
              <div className={styles.buttons}>
                <button className={styles.saveButton} onClick={handleSave}>
                  Save Changes
                </button>
                <button className={styles.cancelButton} onClick={onClose}>
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'account' && (
          <div className={styles.aiSection}>
            {/* Account Information Section */}
            <div className={styles.accountSection}>
              <h3 className={styles.sectionTitle}>Account Information</h3>
              
              <div className={styles.accountRow}>
                <span>Plan:</span>
                <span className={
                  accountData.plan_name === 'Pro' 
                    ? styles.proBadge 
                    : styles.freeBadge
                }>
                  {accountData.plan_name}
                </span>
              </div>
              
              <div className={styles.accountRow}>
                <span>Status:</span>
                <span className={styles.statusBadge}>
                  {accountData.statusLabel}
                </span>
              </div>
              
              <div className={styles.accountRow}>
                <span>Token Allocation:</span>
                <span>{accountData.token_allocation}</span>
              </div>
              
              {accountData.canCancel && (
                <button 
                  className={styles.cancelSubscriptionButton}
                  onClick={handleCancelSubscription}
                >
                  Cancel Subscription
                </button>
              )}
            </div>

            <div className={styles.divider} />

            {/* Token Usage Section */}
            <div className={styles.tokenInfo}>
              <h3 className={styles.sectionTitle}>Token Usage</h3>
              <div className={styles.tokenRow}>
                <span>Balance:</span>
                <span>{tokenData.balance?.toLocaleString()}</span>
              </div>
              <div className={styles.tokenRow}>
                <span>Used:</span>
                <span>{tokenData.used?.toLocaleString()}</span>
              </div>
            </div>

            {/* Transactions Section */}
            <div className={styles.transactionBox}>
              <div className={styles.transactionTitle}>Recent Transactions</div>
              {tokenData.transactions?.map(t => (
                <div key={t.transaction_id} className={styles.tokenRow}>
                  <span>{new Date(t.timestamp).toLocaleString()}</span>
                  <span>{t.request_type}: {t.tokens_used}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SettingsModal;